import { oc } from "@orpc/contract";
import { z } from "zod";
import { ShopPhoto } from "./shop-catalog";

/**
 * Reviews (CONTEXT.md `productReview`, `reviewInvitation`; frontend v2, "Reviews"). About 7 days
 * after shipping the buyer gets one mail with five stars per product; a star opens the shop's
 * `/review?token=…&sterren=N`. The token is the proof of purchase: one review per product per
 * order, `pending` until an employee approves it. Reading reviews and approving them are separate
 * areas of the contract (the catalog and the admin).
 */

/** A review link's token, from the mail. */
export const ReviewToken = z.string().min(1).max(1024);

/** `GET /shop/reviews/invitation?token=`. */
export const ReviewInvitationQuery = z.object({ token: ReviewToken });

/** What the review page shows above the form, for the link it was opened with. */
export const ReviewPage = z.object({
	product: z.object({
		id: z.string(),
		name: z.string(),
		slug: z.string(),
		/** The product's main photo, as the catalog's; `null` without one. */
		photo: ShopPhoto.nullable(),
		/** The pack bought (CONTEXT.md `piecesPerPack`): `null` for a product without pieces. */
		pack: z.object({ piecesPerPack: z.number().int(), pieceNoun: z.string().nullable() }).nullable(),
		/** As the catalog's `sizeLabel`. */
		sizeLabel: z.string().nullable(),
	}),
	/** When the order was placed (ISO 8601). */
	orderedAt: z.iso.datetime(),
	/** The buyer's first name from the order, to start the form with; `""` when unknown. */
	firstName: z.string(),
	/** A review for this product and order exists: the page says so instead of showing the form. */
	alreadyReviewed: z.boolean(),
});
export type ReviewPage = z.infer<typeof ReviewPage>;

/** Optional text: trimmed, and empty is none (`null`). */
const optionalText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.nullish()
		.transform((value) => (value === undefined || value === null || value === "" ? null : value));

/** The longest head, text and first name a review takes, in characters: the form says them too. */
export const REVIEW_TEXT_LIMITS = { title: 80, body: 2000, firstName: 40 } as const;

/** `POST /shop/reviews`: the review, with the link's token. */
export const ReviewSubmission = z.object({
	token: ReviewToken,
	stars: z.number().int().min(1).max(5),
	title: optionalText(REVIEW_TEXT_LIMITS.title),
	body: optionalText(REVIEW_TEXT_LIMITS.body),
	/** As the buyer wants it shown; empty: none (the shop shows "Een koper"). */
	firstName: optionalText(REVIEW_TEXT_LIMITS.firstName),
});
export type ReviewSubmissionInput = z.input<typeof ReviewSubmission>;
export type ReviewSubmission = z.output<typeof ReviewSubmission>;

/** `POST /shop/reviews/opt-out`: the token from the invitation's opt-out link. */
export const ReviewOptOut = z.object({ token: ReviewToken });

/**
 * The shop's review endpoints, public: the token is the proof. Errors on the invitation and the
 * review: `InvalidReviewLink` (400): not a link this shop made; `ReviewLinkExpired` (410): older
 * than 60 days. The review also: `ReviewAlreadyWritten` (409): this product of this order has
 * one; `RateLimited` (429): too many reviews from this IP. The opt-out: `InvalidReviewOptOutLink`
 * (400).
 */
export const ProductReviewContract = {
	/** What the link is for: the product, the order's date, the first name, and whether it was used. */
	invitation: oc
		.route({ method: "GET", path: "/shop/reviews/invitation" })
		.input(ReviewInvitationQuery)
		.output(ReviewPage),
	/** Writes the review, `pending` until an employee approves it. */
	write: oc.route({ method: "POST", path: "/shop/reviews", successStatus: 204 }).input(ReviewSubmission),
	/** No more review invitations to the link's address (its canonical address); again changes nothing. */
	optOut: oc.route({ method: "POST", path: "/shop/reviews/opt-out", successStatus: 204 }).input(ReviewOptOut),
};
